import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  GetContactRequestListProps,
  GetContactRequestListResponse,
  getContactRequests,
  updateContactRequest,
} from "@/services/contact.service";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { CRFRProps, getCRFilterRequest } from "./filter-obj";

interface HandleContactConfirmationProps {
  item: any;
  setSelectedToContact: Dispatch<SetStateAction<any>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  fetchContactRequests: (
    filters?: GetContactRequestListProps,
    showLoad?: boolean,
  ) => Promise<void>;
  currentFilters: GetContactRequestListProps;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const handleContactConfirmation = async ({
  item,
  setSelectedToContact,
  setLoading,
  fetchContactRequests,
  currentFilters,
  navigate,
  fetchPermissions,
}: HandleContactConfirmationProps) => {
  try {
    setSelectedToContact(item);
    setLoading(true);

    await updateContactRequest({ Id: item?.id });

    toaster.create({
      title: "Acción realizada con éxito.",
      type: "success",
    });

    await fetchContactRequests({ ...currentFilters }, false);
  } catch (error) {
    const errorObj = error as AxiosError<{
      message: string;
      tiposFaltantes?: string[];
    }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.CONTACT,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo registrar el usuario mobile.",
      });
    }
    throw error;
  } finally {
    setSelectedToContact(undefined);
    setLoading(false);
  }
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  submitFilters: (values: any) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = ({
  setValue,
  submitFilters,
  getValues,
  setFiltersApplied,
}: ClearFiltersHandlerProps) => {
  setValue("first_name", "");
  setValue("last_name", "");
  setValue("dni", "");
  setValue("email", "");
  setValue("phone", "");
  setValue("status", []);
  setFiltersApplied(false);

  submitFilters?.(getValues());
};

interface SubmitFiltersHandlerProps {
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  fetchContactRequests: (
    filters?: GetContactRequestListProps,
    showLoad?: boolean,
  ) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<GetContactRequestListProps>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  event: FieldValues
}

export const submitFiltersHandler = async ({
  setFiltersApplied,
  fetchContactRequests,
  setCurrentFilters,
  setOpenFilters,
  navigate,
  fetchPermissions,
  event
}: SubmitFiltersHandlerProps) => {
    try {
      setFiltersApplied(true);

      const filterObj = getCRFilterRequest(event as CRFRProps);

      setCurrentFilters(filterObj);
      if (!Object.keys(filterObj)?.length) setFiltersApplied(false);
      await fetchContactRequests(filterObj);

      setOpenFilters(false);
    } catch (error) {
      const errorObj = error as AxiosError<{
        message: string;
        tiposFaltantes?: string[];
      }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.response?.data?.message ??
            "No se pudo aplicar los filtros.",
        });
        throw error;
      }
    }
};

interface FetchContactRequestsHandlerProps {
  setLoadingContactRequests: Dispatch<SetStateAction<boolean>>;
  setContactRequests: Dispatch<SetStateAction<GetContactRequestListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: GetContactRequestListProps;
}

export const fetchContactRequestsHandler = async ({
  setLoadingContactRequests,
  setContactRequests,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters
}: FetchContactRequestsHandlerProps) => {
      try {
      if (showLoad) setLoadingContactRequests(true);
  
      const response = await getContactRequests({
        ...filters,
        "P.Pagina": filters?.["P.Pagina"] ?? 1,
        "P.Records": 10,
      });
  
      setContactRequests(response);
  
      setLoadingContactRequests(false);
      } catch (error) {
        const handled = await handlePermissionError(
          error,
          navigate,
          fetchPermissions,
        );
        if (!handled) {
          toaster.create({
            type: "error",
            title: "No se pudo cargar las solicitudes de contacto.",
          });
        }
      } finally {
        setLoadingContactRequests(false);
      }
}